import { useEffect, useLayoutEffect, useRef } from "react";

import styles from "./index.module.css";

// Idle rings — slow, continuous, subtle
const IDLE_RING_COUNT = 3;
const IDLE_PULSE_PERIOD_MS = 5000;
const IDLE_RING_OPACITY = 0.07;

// Speaking rings — only visible with voice, expand to a larger radius
const SPEAKING_RING_COUNT = 3;
const SPEAKING_PULSE_PERIOD_MS = 2500;
// Noise floor: speaking rings stay at zero opacity until amp exceeds this
const SPEAKING_THRESHOLD = 0.015;

// Fraction of new amplitude blended in each frame (0–1). Lower = smoother/slower reaction to voice; higher = twitchy
const SMOOTHING = 0.12;

interface AudioVisualiserProps {
  analyser: AnalyserNode | null;
}

export const AudioVisualiser = ({ analyser }: AudioVisualiserProps) => {
  const idleRingsRef = useRef<(HTMLDivElement | null)[]>([]);
  const speakingRingsRef = useRef<(HTMLDivElement | null)[]>([]);
  const circleRef = useRef<HTMLDivElement>(null);
  const amplitudeRef = useRef(0);
  const analyserRef = useRef(analyser);
  const rafRef = useRef<number>(0);

  useLayoutEffect(() => {
    analyserRef.current = analyser;
  });

  useEffect(() => {
    const buffer = new Uint8Array(1024);

    const tick = (now: number) => {
      const current = analyserRef.current;

      if (current) {
        // Time-domain waveform: each byte is 0–255, silence = 128
        current.getByteTimeDomainData(buffer.subarray(0, current.frequencyBinCount));
        let sum = 0;
        for (let i = 0; i < current.frequencyBinCount; i++) {
          const v = (buffer[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / current.frequencyBinCount);
        amplitudeRef.current = amplitudeRef.current * (1 - SMOOTHING) + rms * SMOOTHING;
      } else {
        amplitudeRef.current *= 1 - SMOOTHING;
      }

      const amp = amplitudeRef.current;

      // Circle: slow idle breathe + amplitude boost when speaking
      const breathe = Math.sin((now * 2 * Math.PI) / 6000) * 0.03;
      if (circleRef.current) {
        circleRef.current.style.transform = `scale(${1 + breathe + amp * 0.2})`;
      }

      for (let i = 0; i < IDLE_RING_COUNT; i++) {
        const ring = idleRingsRef.current[i];
        if (!ring) continue;
        const t = (now / IDLE_PULSE_PERIOD_MS + i / IDLE_RING_COUNT) % 1;
        // Start at 0.6 so the ring is born at the same radius as the 3rem circle (3/5 = 0.6)
        ring.style.transform = `translate(-50%, -50%) scale(${0.6 + t * 1.9})`;
        ring.style.opacity = `${Math.pow(1 - t, 0.4) * IDLE_RING_OPACITY}`;
      }

      const speakingAmp = Math.max(0, amp - SPEAKING_THRESHOLD);
      for (let i = 0; i < SPEAKING_RING_COUNT; i++) {
        const ring = speakingRingsRef.current[i];
        if (!ring) continue;
        const t = (now / SPEAKING_PULSE_PERIOD_MS + i / SPEAKING_RING_COUNT) % 1;
        ring.style.transform = `translate(-50%, -50%) scale(${1 + t * (3 + speakingAmp * 5)})`;
        ring.style.opacity = `${Math.pow(1 - t, 0.8) * Math.min(speakingAmp * 10, 1)}`;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div className={styles.container}>
      {Array.from({ length: IDLE_RING_COUNT }, (_, i) => (
        <div
          key={`idle-${i}`}
          ref={(el) => {
            idleRingsRef.current[i] = el;
          }}
          className={styles.ring}
        />
      ))}
      {Array.from({ length: SPEAKING_RING_COUNT }, (_, i) => (
        <div
          key={`speaking-${i}`}
          ref={(el) => {
            speakingRingsRef.current[i] = el;
          }}
          className={styles.speakingRing}
        />
      ))}
      <div ref={circleRef} className={styles.circle} />
    </div>
  );
};
